import get from 'lodash/get';
import { getFormNodeOptions, getFormNodeOptionValue } from 'common/entities/formNode/selectors';

const MULTIPLE_SELECTED_VALUE_SEPARATOR = '||';

/**
 * Receives if the option should be unset and if it's selected or not
 * and returns a boolean for it's selected state.
 * @param {boolean} unsetOthers
 * @param {boolean} isSelected
 * @return {boolean}
 */
export const getIsOptionSelectedOrUnset = (unsetOthers, isSelected = false) => (unsetOthers ? false : isSelected);

/**
 * Returns the value of a node option given their ids in case the option is selected.
 * @param {Object} state
 * @param {string} nodeId
 * @param {string} optionId
 * @param {boolean} selected
 * @return {boolean}
 */
export const getNodeOptionValueIfSelected = (state, nodeId, optionId, selected) =>
    selected ? getFormNodeOptionValue(state, nodeId, optionId) : null;

/**
 * Returns the value of multiple node options given the id of the selected one and the node id,
 * so it returns a joined string of all their values separated by '||' operators.
 * @param {Object} state
 * @param {string} nodeId
 * @param {string} optionId
 * @param {boolean} selected
 * @return {string}
 */
export const getAllNodeOptionsIfSelected = (state, nodeId, optionId, selected) => {
    const newValue = getNodeOptionValueIfSelected(state, nodeId, optionId, selected);

    const formNodeOptions = getFormNodeOptions(state, nodeId);
    const selectedOptions = formNodeOptions
        .filter((option) => option.selected || option.id === optionId)
        .map((option) => (option.id === optionId ? newValue : option.value))
        .filter(Boolean);

    return selectedOptions.join(MULTIPLE_SELECTED_VALUE_SEPARATOR);
};

/**
 * Return a new set of selected options for a given node.
 * This makes the first option found to be the one selected so in case we change from multicheckbox
 * to radio or dropdown we only have on selected and not multiple.
 * @param {Object} node
 * @return {Object}
 */
export const getNodeOptionsWithFirstSelectedOnly = (node) => {
    const nodeOptions = get(node, 'config.options', {});
    const nodeOptionsEntities = get(nodeOptions, 'entities', {});
    const nodeEntitiesArray = Object.values(nodeOptionsEntities);
    const firstSelectedEntity = nodeEntitiesArray.find((entity) => entity.selected);

    const newNodeOptionsEntities = nodeEntitiesArray.reduce(
        (acc, entity) => ({
            ...acc,
            [entity.id]: {
                ...entity,
                selected: entity.id === get(firstSelectedEntity, 'id'),
            },
        }),
        {},
    );

    return {
        ...nodeOptions,
        entities: newNodeOptionsEntities,
        result: Object.keys(newNodeOptionsEntities),
    };
};

/**
 * Returns the value of the first selected entity.
 * @param {Object} state
 * @param {Array} entities
 * @return {string}
 */
export const getFirstSelectedEntityOptionValue = (entities) => {
    const selectedEntityOption = Object.values(entities).find((entity) => entity.selected);
    return selectedEntityOption ? selectedEntityOption.value : null;
};
